import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MUTED_GRAY, NOPING_GREEN } from '../constants';

const RED = '#EF4444';

function generateHighPingPoints(numPoints: number, seed: number): number[] {
  const points: number[] = [];
  for (let i = 0; i < numPoints; i += 1) {
    const base = 35 + Math.sin(i * 0.7 + seed) * 15 + Math.cos(i * 1.3 + seed * 0.5) * 10;
    const jitter = Math.sin(i * 3.7 + seed * 2.1) * 12;
    points.push(Math.max(10, Math.min(80, base + jitter)));
  }
  return points;
}

function generateLowPingPoints(numPoints: number, seed: number): number[] {
  const points: number[] = [];
  for (let i = 0; i < numPoints; i += 1) {
    const base = 150 + Math.sin(i * 0.5 + seed) * 3 + Math.cos(i * 0.8 + seed * 0.3) * 2;
    points.push(base);
  }
  return points;
}

function pointsToPath(points: number[], width: number): string {
  const step = width / (points.length - 1);
  return points.map((y, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y.toFixed(1)}`).join(' ');
}

export function PingSliderCompare() {
  const { t } = useTranslation('components/trial/lp/v1c');
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  const svgRef = useRef<SVGSVGElement>(null);
  const [sliderX, setSliderX] = useState(0.5);
  const [isDragging, setIsDragging] = useState(false);
  const [animSeed, setAnimSeed] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    let raf: number;
    const animateFrame = () => {
      setAnimSeed((prev) => prev + 0.02);
      raf = requestAnimationFrame(animateFrame);
    };
    raf = requestAnimationFrame(animateFrame);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  const SVG_W = 500;
  const SVG_H = 160;
  const NUM_POINTS = 35;
  const dividerX = sliderX * SVG_W;

  const highPoints = generateHighPingPoints(NUM_POINTS, animSeed);
  const lowPoints = generateLowPingPoints(NUM_POINTS, animSeed);

  const highPath = pointsToPath(highPoints, SVG_W);
  const lowPath = pointsToPath(lowPoints, SVG_W);

  const highFill = `${highPath} L${SVG_W},${SVG_H} L0,${SVG_H} Z`;
  const lowFill = `${lowPath} L${SVG_W},${SVG_H} L0,${SVG_H} Z`;

  const avgHighPing = Math.round(highPoints.reduce((a, b) => a + b, 0) / highPoints.length);
  const displayHighPing = Math.round(avgHighPing * 1.8);
  const displayLowPing = Math.round(3 + Math.sin(animSeed * 0.5) * 2);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      setSliderX(Math.max(0.05, Math.min(0.95, x)));
    },
    [isDragging]
  );

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    if (svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      setSliderX(Math.max(0.05, Math.min(0.95, x)));
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div ref={ref} className='mx-auto w-full max-w-md'>
      <div className='mb-2 flex items-center justify-between px-1'>
        <div className='flex items-center gap-1.5'>
          <span className='h-0.5 w-2.5 rounded-full' style={{ backgroundColor: RED }} />
          <span className='font-mono text-[10px]' style={{ color: RED }}>
            {t('ping_slider.without')}: {displayHighPing}ms
          </span>
        </div>
        <div className='flex items-center gap-1.5'>
          <span className='h-0.5 w-2.5 rounded-full' style={{ backgroundColor: NOPING_GREEN }} />
          <span className='font-mono text-[10px]' style={{ color: NOPING_GREEN }}>
            {t('ping_slider.with_noping')}: {displayLowPing}ms
          </span>
        </div>
      </div>

      <motion.div className='relative cursor-ew-resize overflow-hidden rounded-lg border' style={{ borderColor: 'rgba(255,255,255,0.08)', backgroundColor: '#060608' }} initial={{ opacity: 0, y: 15 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
        <svg ref={svgRef} viewBox={`0 0 ${SVG_W} ${SVG_H}`} className='w-full' style={{ height: 'auto', aspectRatio: `${SVG_W}/${SVG_H}` }} preserveAspectRatio='none' onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp}>
          <defs>
            <linearGradient id='eu-sl-redFill' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={RED} stopOpacity='0.18' />
              <stop offset='100%' stopColor={RED} stopOpacity='0' />
            </linearGradient>
            <linearGradient id='eu-sl-greenFill' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={NOPING_GREEN} stopOpacity='0.2' />
              <stop offset='100%' stopColor={NOPING_GREEN} stopOpacity='0' />
            </linearGradient>
            <clipPath id='eu-sl-clipLeft'>
              <rect x='0' y='0' width={dividerX} height={SVG_H} />
            </clipPath>
            <clipPath id='eu-sl-clipRight'>
              <rect x={dividerX} y='0' width={SVG_W - dividerX} height={SVG_H} />
            </clipPath>
            <filter id='eu-sl-glow'>
              <feGaussianBlur stdDeviation='2' result='blur' />
              <feMerge>
                <feMergeNode in='blur' />
                <feMergeNode in='SourceGraphic' />
              </feMerge>
            </filter>
          </defs>

          {Array.from({ length: 5 }).map((_, i) => (
            <line key={i} x1='0' y1={(i + 1) * 26} x2={SVG_W} y2={(i + 1) * 26} stroke='#ffffff' strokeWidth='0.5' opacity='0.04' />
          ))}

          <g clipPath='url(#eu-sl-clipLeft)'>
            <path d={highFill} fill='url(#eu-sl-redFill)' />
            <path d={highPath} fill='none' stroke={RED} strokeWidth='2' opacity='0.8' />
          </g>

          <g clipPath='url(#eu-sl-clipRight)'>
            <path d={lowFill} fill='url(#eu-sl-greenFill)' />
            <path d={lowPath} fill='none' stroke={NOPING_GREEN} strokeWidth='2.5' filter='url(#eu-sl-glow)' />
          </g>

          <line x1={dividerX} y1='0' x2={dividerX} y2={SVG_H} stroke='#ffffff' strokeWidth='1.5' opacity='0.5' strokeDasharray='4 4' />

          <circle cx={dividerX} cy={SVG_H / 2} r='10' fill='#ffffff' opacity='0.12' />
          <circle cx={dividerX} cy={SVG_H / 2} r='6' fill='#ffffff' opacity='0.3' />
          <path d={`M${dividerX - 3},${SVG_H / 2 - 3} L${dividerX - 6},${SVG_H / 2} L${dividerX - 3},${SVG_H / 2 + 3}`} stroke='#fff' strokeWidth='1.2' fill='none' strokeLinecap='round' />
          <path d={`M${dividerX + 3},${SVG_H / 2 - 3} L${dividerX + 6},${SVG_H / 2} L${dividerX + 3},${SVG_H / 2 + 3}`} stroke='#fff' strokeWidth='1.2' fill='none' strokeLinecap='round' />

          <text x={Math.max(dividerX / 2, 45)} y='16' textAnchor='middle' fill={RED} fontSize='9' fontFamily='monospace' fontWeight='bold' opacity='0.6'>
            {t('ping_slider.without_label')}
          </text>
          <text x={Math.min(dividerX + (SVG_W - dividerX) / 2, SVG_W - 45)} y='16' textAnchor='middle' fill={NOPING_GREEN} fontSize='9' fontFamily='monospace' fontWeight='bold' opacity='0.6'>
            {t('ping_slider.with_label')}
          </text>
        </svg>

        <motion.div className='absolute bottom-1.5 left-1/2 -translate-x-1/2 rounded-full px-2 py-0.5 font-mono text-[9px]' style={{ color: MUTED_GRAY, backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }} animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 2, repeat: Infinity }}>
          {t('ping_slider.drag_hint')}
        </motion.div>
      </motion.div>
    </div>
  );
}
